import { ArgumentNotSuppliedError } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import { Poller } from "client_legacy/ndaw/poller";
import { HttpRequestError } from "client/Api/httpClient";
import {
	ensureNotNil,
	isNil,
	debounce
} from "client/Misc/utils";
import { fromArray } from "client_legacy/ndaw/mapHelpers";

export interface InterestedParty<TPartyIdentifier, TResourceId> {
	partyId: TPartyIdentifier;
	resourceId: TResourceId;
	informAboutGlobalRetrievalFailure(): void;
	informAboutConsecutiveErrors(): void;
}

type Registration<TInterestedParty, TInterests> = {
	party: TInterestedParty;
	interestedIn: TInterests;
};

export type GlobalFailureCacheItem<TInterests> = {
	wasGlobalFailure: true;
	retrievedInterests: TInterests;
};

export type SuccessfulCacheItem<TInterests, TResponseData> = {
	wasGlobalFailure: false;
	retrievedInterests: TInterests;
	data: TResponseData;
};

export type CacheItem<TInterests, TResponseData> =
	| GlobalFailureCacheItem<TInterests>
	| SuccessfulCacheItem<TInterests, TResponseData>;

type Interests = {};

export abstract class LiveInformationProvider<
	TOptions extends { intervalInSeconds: number; },
	TPartyIdentifier extends Guid|string|number,
	TResourceId,
	TInterests extends Interests,
	TInterestedParty extends InterestedParty<TPartyIdentifier, TResourceId>,
	TResponseData
> {

	private readonly _poller: Poller;

	protected readonly _registeredParties: Map<TPartyIdentifier, Registration<TInterestedParty, TInterests>>;
	private readonly _pendingPartiesForDataRetrieval: Map<TPartyIdentifier, TInterests>;
	private readonly _debouncedDataRetrieval: () => void;

	protected readonly _cache: Map<TResourceId, CacheItem<TInterests, TResponseData>>;
	private _consecutivePollingErrors: number;
	protected readonly options: TOptions;

	private get hasExceededConsecutivePollingErrors(): boolean {
		return this._consecutivePollingErrors > 2;
	}

	public get hasRegisteredParties(): boolean {
		return this._registeredParties.size > 0;
	}

	constructor(options: TOptions) {
		if (!options) {
			throw new ArgumentNotSuppliedError("options");
		}

		this.options = options;
		this._poller = new Poller({
			intervalInSeconds: options.intervalInSeconds,
			callback: () => this.loadUpdates()
		});

		this._registeredParties = new Map<TPartyIdentifier, Registration<TInterestedParty, TInterests>>();
		this._pendingPartiesForDataRetrieval = new Map<TPartyIdentifier, TInterests>();
		this._cache = this.createResourceMap();
		this._consecutivePollingErrors = 0;

		this._debouncedDataRetrieval = debounce(
			() => {
				// Since it is debounced it can fire the callback
				// when the party already unregistered again
				if (this._pendingPartiesForDataRetrieval.size === 0) {
					return;
				}

				this.getPendingDataAsync();
			},
			200
		);
	}

	public register(party: TInterestedParty, interests: TInterests): void {
		if (!party) {
			throw new ArgumentNotSuppliedError("party");
		}

		if (!interests) {
			throw new ArgumentNotSuppliedError("interests");
		}

		this._registeredParties.set(
			party.partyId,
			{
				party: party,
				interestedIn: interests
			}
		);

		this.queuePartyForDataRetrieval(party, interests);

		dontExecuteOnCriticalPath(
			() => {
				const latestVersionParty: TInterestedParty | undefined = this._registeredParties
					.get(party.partyId)
					?.party;

				if (isNil(latestVersionParty)) {
					return;
				}

				this.updateParty(latestVersionParty);
			}
		);
	}

	public forceReload() {
		for (const registration of this._registeredParties.values()) {
			this._pendingPartiesForDataRetrieval.set(registration.party.partyId, registration.interestedIn);
		}
		this._cache.clear();
		this._consecutivePollingErrors = 0;
		this._debouncedDataRetrieval();
	}

	protected queuePartyForDataRetrieval(party: TInterestedParty, interests: TInterests) {
		this._pendingPartiesForDataRetrieval.set(party.partyId, interests);
		this._debouncedDataRetrieval();
	}

	protected getInterestsForEnsuredParty(party: TInterestedParty): TInterests {
		return ensureNotNil(this._registeredParties.get(party.partyId))
			.interestedIn;
	}

	public changeInterests(party: TInterestedParty, interests: TInterests) {
		// From outside it is a separate api, but implementation is the same as
		// register
		this.register(party, interests);
	}

	public unregister(party: TInterestedParty): void {
		if (!party) {
			throw new ArgumentNotSuppliedError("party");
		}

		this._registeredParties.delete(party.partyId);
		this._pendingPartiesForDataRetrieval.delete(party.partyId);

		if (this._registeredParties.size === 0) {
			this._poller.stop();
		}
	}

	private async getPendingDataAsync() {
		if (this.hasExceededConsecutivePollingErrors) {
			this._pendingPartiesForDataRetrieval.clear();
			this.updateAllParties();
			return;
		}

		if (!this._poller.isActive) {
			// Let the polling take care of all data retrieval
			this._pendingPartiesForDataRetrieval.clear();
			this._poller.start();
			return;
		}

		const interestsMerger: IInterestsMerger<TResourceId, TInterests> = this.createInterestsMerger();

		const partiesToToUpdate: TPartyIdentifier[] = [];
		for (const [partyId, newInterests] of this._pendingPartiesForDataRetrieval.entries()) {
			const { party } = ensureNotNil(this._registeredParties.get(partyId));
			const { resourceId } = party;

			const cacheItem: CacheItem<TInterests, TResponseData> | undefined = this._cache.get(resourceId);
			let missingCacheData: Interests = {} as any as Interests;

			if (!cacheItem) {
				missingCacheData = newInterests;
			} else {
				const cachedInterests: TInterests = cacheItem.retrievedInterests;
				missingCacheData = interestsMerger.computeMissingInterests(
					cachedInterests,
					newInterests
				);
			}

			const isAllDataPresent = !interestsMerger.hasAnyActualInterests(missingCacheData as TInterests);
			if (isAllDataPresent) {
				continue;
			}

			partiesToToUpdate.push(partyId);
			interestsMerger.add(resourceId, missingCacheData as TInterests);
		}

		this._pendingPartiesForDataRetrieval.clear();

		const entries: ReadonlyArray<[TResourceId, TInterests]> = interestsMerger.entries();

		if (entries.length === 0) {
			return;
		}

		try {
			await this.updateCacheFromTheBackendAsync(entries, { clearCacheBeforeFilling: false });
			this._consecutivePollingErrors = 0;
		} catch (e) {
			this.processError(e);
		} finally {
			for (const partyId of partiesToToUpdate) {
				const registration: Registration<TInterestedParty, TInterests> | undefined = this._registeredParties.get(partyId);
				if (!registration) {
					// It was unregistered in the meantime
					continue;
				}

				this.updateParty(registration.party);
			}
		}
	}

	protected async updateCacheFromTheBackendAsync(
		entries: ReadonlyArray<[TResourceId, TInterests]>,
		{ clearCacheBeforeFilling }: { clearCacheBeforeFilling: boolean; }
	): Promise<void> {
		try {
			const responses: ReadonlyArray<[TResourceId, TResponseData]> = await this.getDataFromBackend(entries);
			const responseMap: Map<TResourceId, TResponseData> = fromArray(() => this.createResourceMap(), responses, r => r[0], r => r[1]);

			if (clearCacheBeforeFilling) {
				this._cache.clear();
			}

			for (const requestEntry of entries) {
				const [resourceId, interestedIn] = requestEntry;
				if (!responseMap.has(resourceId)) {
					throw new Error(`Data for requested resourceId ${resourceId} was not received from the server`);
				}

				const data: TResponseData = responseMap.get(resourceId)!;

				const cacheItem: CacheItem<TInterests, TResponseData> | undefined = this._cache.get(resourceId);
				if (!cacheItem || cacheItem.wasGlobalFailure) {
					this._cache.set(
						resourceId,
						{
							wasGlobalFailure: false,
							retrievedInterests: interestedIn,
							data: data
						}
					);
				} else {
					this.updateCacheItem(cacheItem, [interestedIn, data]);
				}
			}
		} catch (e) {
			if (clearCacheBeforeFilling) {
				this._cache.clear();
			}

			for (const requestEntry of entries) {
				const [category, interestedIn] = requestEntry;
				this._cache.set(
					category,
					{
						wasGlobalFailure: true,
						retrievedInterests: interestedIn
					}
				);
			}

			throw e;
		}
	}

	private async loadUpdates(): Promise<void> {
		const interestsMerger: IInterestsMerger<TResourceId, TInterests> = this.createInterestsMerger();

		for (const registration of this._registeredParties.values()) {
			const { resourceId } = registration.party;
			interestsMerger.add(resourceId, registration.interestedIn);
		}

		const entriesWithActualInterests: ReadonlyArray<[TResourceId, TInterests]> = interestsMerger.entries()
			.filter(([_, interests]) => interestsMerger.hasAnyActualInterests(interests));

		if (entriesWithActualInterests.length === 0) {
			// We have parties which are not interested in any information, skip the call to the backend
			return;
		}

		try {
			await this.updateCacheFromTheBackendAsync(entriesWithActualInterests, { clearCacheBeforeFilling: true });
			this._consecutivePollingErrors = 0;
		} catch (e) {
			this.processError(e);
		} finally {
			this.updateAllParties();
		}
	}

	private processError(e: unknown): void {
		if (e instanceof HttpRequestError && (e.statusCode < 200 || e.statusCode >= 400)) {
			this._consecutivePollingErrors++;
			if (this.hasExceededConsecutivePollingErrors) {
				console.error("Stopping the poller because there were too many consecutive errors");
				this._poller.stop();
			}
		}
		throw e;
	}

	protected updateAllParties() {
		for (const registration of this._registeredParties.values()) {
			this.updateParty(registration.party);
		}
	}

	private updateParty(party: TInterestedParty) {
		if (!party) {
			throw new ArgumentNotSuppliedError("party");
		}

		if (this.hasExceededConsecutivePollingErrors) {
			this.informPartyAboutConsecutiveErrors(party);
			return;
		}

		const { resourceId } = party;
		const cacheItem: CacheItem<TInterests, TResponseData> | undefined = this._cache.get(resourceId);
		if (!cacheItem) {
			return;
		}

		if (cacheItem.wasGlobalFailure) {
			this.informAboutGlobalRetrievalFailure(party);
		} else {
			this.informParty(party, [cacheItem.retrievedInterests, cacheItem.data]);
		}
	}

	protected clearCacheForResource(resourceId: TResourceId) {
		this._cache.delete(resourceId);
	}

	protected abstract getDataFromBackend(entries: ReadonlyArray<[TResourceId, TInterests]>): Promise<ReadonlyArray<[TResourceId, TResponseData]>>;

	protected abstract updateCacheItem(cacheItem: SuccessfulCacheItem<TInterests, TResponseData>, response: [TInterests, TResponseData]): void;

	protected mutateCacheItem(
		resourceId: TResourceId,
		reducer: (cacheItem: CacheItem<TInterests, TResponseData>) => CacheItem<TInterests, TResponseData>
	): void {
		const cacheItem: CacheItem<TInterests, TResponseData> | undefined = this._cache.get(resourceId);
		if (cacheItem) {
			const newCacheItem: CacheItem<TInterests, TResponseData> = reducer(cacheItem);
			this._cache.set(resourceId, newCacheItem);

			const map = this.createResourceMap<undefined>();
			map.set(resourceId, undefined);
			for (const registeredParty of this._registeredParties.values()) {
				const party: TInterestedParty = registeredParty.party;
				if (!map.has(party.resourceId)) {
					continue;
				}
				this.updateParty(party);
			}
		}
	}

	protected informPartyAboutConsecutiveErrors(party: TInterestedParty) {
		if (party.informAboutConsecutiveErrors) {
			party.informAboutConsecutiveErrors();
		}
	}

	protected informAboutGlobalRetrievalFailure(party: TInterestedParty) {
		if (party.informAboutGlobalRetrievalFailure) {
			party.informAboutGlobalRetrievalFailure();
		}
	}

	protected abstract informParty(party: TInterestedParty, cacheItem: [TInterests, TResponseData]): void;

	protected createInterestsMerger(): IInterestsMerger<TResourceId, TInterests> {
		return new DefaultInterestsMerger<TResourceId, TInterests>(this.createResourceMap());
	}

	protected createResourceMap<TValue>(): Map<TResourceId, TValue> {
		return new Map();
	}
}

export class DefaultInterestsMerger<TResourceId, TInterests extends { [property: string]: boolean; }>
	implements IInterestsMerger<TResourceId, TInterests> {

	private readonly _interestsMap: Map<TResourceId, TInterests>;

	constructor(interestsMap: Map<TResourceId, TInterests>) {
		this._interestsMap = interestsMap;
	}

	public add(resourceId: TResourceId, interestedIn: TInterests) {
		let interests: Record<string, boolean> | undefined = this._interestsMap.get(resourceId);
		if (!interests) {
			interests = {};
			this._interestsMap.set(resourceId, interests as TInterests);
		}

		// We aggregate the interests to reduce the load on the backend
		for (const key of Object.keys(interestedIn)) {
			if (interestedIn[key]) {
				interests[key] = true;
			}
		}
	}

	public entries(): ReadonlyArray<[TResourceId, TInterests]> {
		return [...this._interestsMap.entries()];
	}

	public hasAnyActualInterests(interests: TInterests): boolean {
		for (const key of Object.keys(interests)) {
			if (interests[key]) {
				return true;
			}
		}
		return false;
	}

	public computeMissingInterests(
		current: TInterests,
		desired: TInterests
	): TInterests {
		const result: Record<string, boolean> = {};

		for (const key of Object.keys(desired)) {
			if (desired[key] && !current[key]) {
				result[key] = true;
			}
		}

		return result as TInterests;
	}
}

export interface IInterestsMerger<TResourceId, TInterests extends Interests> {
	entries(): ReadonlyArray<[TResourceId, TInterests]>;
	add(resourceId: TResourceId, interestedIn: TInterests): void;
	hasAnyActualInterests(interests: TInterests): boolean;
	computeMissingInterests(current: TInterests, desired: TInterests): TInterests;
}

export function dontExecuteOnCriticalPath(callback: Function) {
	// When (re)building and creating all objects at once can have
	// a "locked" up experience (especially in browsers like IE). To
	// make the experience nicer we move certain parts of the critical
	// path (i.e. we delay their executing till later) resulting in a
	// shorter "locked" up experience and having the less critical information
	// shortly after
	setTimeout(callback, 0);
}
